import { type ProviderKind, type ThreadId } from "@cmmd-forge/contracts";
import { scopeThreadRef, scopedThreadKey } from "@cmmd-forge/client-runtime";
import { latestCompletedToolActivityId } from "../../queuedMessageStore";
import { type ComposerModelSelection } from "./useComposerModelSelection";
import type { ComposerDisabledReason } from "./composerTurnPlan";
import { useChatTurnActionController } from "./useChatTurnActionController";
import { type useChatRouteRuntime } from "./useChatRouteRuntime";
import { type useChatThreadControlsRuntime } from "./useChatThreadControlsRuntime";
import { type useChatThreadRuntimeState } from "./useChatThreadRuntimeState";

export function useChatThreadActionsRuntime(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly threadRuntime: ReturnType<typeof useChatThreadRuntimeState>;
  readonly controlsRuntime: ReturnType<typeof useChatThreadControlsRuntime>;
  readonly routeThreadId: ThreadId;
  readonly lockedProvider: ProviderKind | null;
  readonly composerModelSelection: ComposerModelSelection;
  readonly composerDisabledReason: ComposerDisabledReason;
  readonly onOpenUsageLimits: () => boolean;
  readonly onAcceptedTurnStart: (threadId: ThreadId) => void;
}) {
  return useChatTurnActionController({
    environmentId: input.routeRuntime.route.environmentId,
    activeThread: input.routeRuntime.bootstrap.activeThread,
    revertTurnCountByUserMessageId: input.threadRuntime.timeline.revertTurnCountByUserMessageId,
    checkpoint: buildCheckpointInput(input),
    gitHub: buildGitHubInput(input),
    planFollowUp: buildPlanFollowUpInput(input),
    send: buildSendInput(input),
    pendingAutoSubmit: buildPendingAutoSubmitInput(input),
    providerModel: buildProviderModelInput(input),
  });
}

function buildCheckpointInput(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly threadRuntime: ReturnType<typeof useChatThreadRuntimeState>;
}) {
  const { route, bootstrap, runtimeFlags } = input.routeRuntime;
  return {
    environmentId: route.environmentId,
    activeThread: bootstrap.activeThread,
    phase: input.threadRuntime.activity.phase,
    isSendBusy: input.threadRuntime.dispatch.isSendBusy,
    isConnecting: runtimeFlags.isConnecting,
    isRevertingCheckpoint: runtimeFlags.isRevertingCheckpoint,
    setIsRevertingCheckpoint: runtimeFlags.setIsRevertingCheckpoint,
    setThreadError: bootstrap.setThreadError,
  };
}

function buildGitHubInput(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly threadRuntime: ReturnType<typeof useChatThreadRuntimeState>;
  readonly controlsRuntime: ReturnType<typeof useChatThreadControlsRuntime>;
  readonly composerModelSelection: ComposerModelSelection;
}) {
  const { route, bootstrap, composer, runtimeFlags } = input.routeRuntime;
  const { activity, dispatch, timeline } = input.threadRuntime;
  const { scroll } = input.controlsRuntime;
  return {
    environmentId: route.environmentId,
    activeThread: bootstrap.activeThread,
    activePendingGitHubConfirmation: timeline.activePendingGitHubConfirmation,
    phase: activity.phase,
    isSendBusy: dispatch.isSendBusy,
    isConnecting: runtimeFlags.isConnecting,
    sendInFlightRef: runtimeFlags.sendInFlightRef,
    composerDraftTarget: bootstrap.composerDraftTarget,
    setComposerDraftPrompt: composer.setComposerDraftPrompt,
    promptRef: composer.promptRef,
    composerRef: composer.composerRef,
    composerModelSelection: input.composerModelSelection,
    beginLocalDispatch: dispatch.beginLocalDispatch,
    resetLocalDispatch: dispatch.resetLocalDispatch,
    runtimeMode: bootstrap.runtimeMode,
    interactionMode: bootstrap.interactionMode,
    persistThreadSettingsForNextTurn: input.controlsRuntime.persistence,
    setThreadError: bootstrap.setThreadError,
    setOptimisticUserMessages: timeline.setOptimisticUserMessages,
    showScrollDebouncer: scroll.showScrollDebouncer,
    setShowScrollToBottom: scroll.setShowScrollToBottom,
    legendListRef: scroll.legendListRef,
    scheduleComposerFocus: composer.scheduleComposerFocus,
  };
}

function buildPlanFollowUpInput(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly threadRuntime: ReturnType<typeof useChatThreadRuntimeState>;
  readonly controlsRuntime: ReturnType<typeof useChatThreadControlsRuntime>;
  readonly routeThreadId: ThreadId;
  readonly composerModelSelection: ComposerModelSelection;
}) {
  const { route, bootstrap, composer, runtimeFlags } = input.routeRuntime;
  const { projectContext, activity, dispatch, timeline } = input.threadRuntime;
  const { env, panels, scroll } = input.controlsRuntime;
  return {
    environmentId: route.environmentId,
    routeThreadId: input.routeThreadId,
    activeThread: bootstrap.activeThread,
    activeProject: projectContext.activeProject,
    activeProposedPlan: activity.activeProposedPlan,
    activeThreadBranch: env.activeThreadBranch,
    isServerThread: bootstrap.isServerThread,
    isSendBusy: dispatch.isSendBusy,
    isConnecting: runtimeFlags.isConnecting,
    sendInFlightRef: runtimeFlags.sendInFlightRef,
    composerModelSelection: input.composerModelSelection,
    runtimeMode: bootstrap.runtimeMode,
    beginLocalDispatch: dispatch.beginLocalDispatch,
    resetLocalDispatch: dispatch.resetLocalDispatch,
    setThreadError: bootstrap.setThreadError,
    showScrollDebouncer: scroll.showScrollDebouncer,
    setShowScrollToBottom: scroll.setShowScrollToBottom,
    legendListRef: scroll.legendListRef,
    isAtEndRef: scroll.isAtEndRef,
    setOptimisticUserMessages: timeline.setOptimisticUserMessages,
    persistThreadSettingsForNextTurn: input.controlsRuntime.persistence,
    setComposerDraftInteractionMode: composer.setComposerDraftInteractionMode,
    openPlanPanel: panels.openPlanPanel,
  };
}

/** Builds submission inputs with a queue scoped to the environment, thread, and dispatch provider. */
function buildSendInput(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly threadRuntime: ReturnType<typeof useChatThreadRuntimeState>;
  readonly controlsRuntime: ReturnType<typeof useChatThreadControlsRuntime>;
  readonly composerDisabledReason: ComposerDisabledReason;
  readonly composerModelSelection: ComposerModelSelection;
  readonly onOpenUsageLimits: () => boolean;
  readonly onAcceptedTurnStart: (threadId: ThreadId) => void;
}) {
  const { route, bootstrap, composer, runtimeFlags } = input.routeRuntime;
  const { projectContext, activity, pendingInput, dispatch, timeline } = input.threadRuntime;
  const { env, panels, scroll, composerModes } = input.controlsRuntime;
  return {
    environmentId: route.environmentId,
    primaryEnvironmentId: bootstrap.primaryEnvironmentId,
    queueContext: bootstrap.activeThread
      ? {
          threadKey: JSON.stringify([
            scopedThreadKey(scopeThreadRef(route.environmentId, bootstrap.activeThread.id)),
            input.composerModelSelection.provider,
          ]),
          phase: activity.phase,
          latestToolActivityId: latestCompletedToolActivityId(
            bootstrap.activeThread.activities ?? [],
          ),
          followUpQueueEnabled: true,
        }
      : undefined,
    activeThread: bootstrap.activeThread,
    activeProject: projectContext.activeProject,
    draftThread: bootstrap.draftThread,
    isSendBusy: dispatch.isSendBusy,
    isConnecting: runtimeFlags.isConnecting,
    isServerThread: bootstrap.isServerThread,
    isLocalDraftThread: bootstrap.isLocalDraftThread,
    sendEnvMode: env.sendEnvMode,
    activeThreadBranch: env.activeThreadBranch,
    composerDisabledReason: input.composerDisabledReason,
    hasActivePendingProgress: Boolean(pendingInput.activePendingProgress),
    showPlanFollowUpPrompt: activity.showPlanFollowUpPrompt,
    activeProposedPlan: activity.activeProposedPlan,
    canvasOpen: panels.canvasOpen,
    sendInFlightRef: runtimeFlags.sendInFlightRef,
    promptRef: composer.promptRef,
    composerImagesRef: composer.composerImagesRef,
    composerTerminalContextsRef: composer.composerTerminalContextsRef,
    composerRef: composer.composerRef,
    composerModelSelection: input.composerModelSelection,
    composerDraftTarget: bootstrap.composerDraftTarget,
    readComposerDraft: composer.readComposerDraft,
    stageComposerSubmission: composer.stageComposerSubmission,
    setStagedComposerExecutionIntent: composer.setStagedComposerExecutionIntent,
    clearStagedComposerSubmission: composer.clearStagedComposerSubmission,
    clearComposerDraftContent: composer.clearComposerDraftContent,
    setComposerDraftPrompt: composer.setComposerDraftPrompt,
    addComposerDraftImages: composer.addComposerDraftImages,
    setComposerDraftFiles: composer.setComposerDraftFiles,
    setComposerDraftTerminalContexts: composer.setComposerDraftTerminalContexts,
    setOptimisticUserMessages: timeline.setOptimisticUserMessages,
    setThreadError: bootstrap.setThreadError,
    beginLocalDispatch: dispatch.beginLocalDispatch,
    resetLocalDispatch: dispatch.resetLocalDispatch,
    runtimeMode: bootstrap.runtimeMode,
    interactionMode: bootstrap.interactionMode,
    persistThreadSettingsForNextTurn: input.controlsRuntime.persistence,
    isAtEndRef: scroll.isAtEndRef,
    showScrollDebouncer: scroll.showScrollDebouncer,
    setShowScrollToBottom: scroll.setShowScrollToBottom,
    legendListRef: scroll.legendListRef,
    onAdvanceActivePendingUserInput: pendingInput.onAdvanceActivePendingUserInput,
    handleInteractionModeChange: composerModes.handleInteractionModeChange,
    onToggleCanvas: panels.onToggleCanvas,
    onOpenUsageLimits: input.onOpenUsageLimits,
    onAcceptedTurnStart: input.onAcceptedTurnStart,
  };
}

function buildPendingAutoSubmitInput(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly threadRuntime: ReturnType<typeof useChatThreadRuntimeState>;
  readonly composerDisabledReason: ComposerDisabledReason;
}) {
  const { route, bootstrap, composer, runtimeFlags } = input.routeRuntime;
  return {
    pendingAutoSubmit: bootstrap.composerPendingAutoSubmit,
    activeThread: bootstrap.activeThread,
    activeProjectExists: Boolean(input.threadRuntime.projectContext.activeProject),
    environmentId: route.environmentId,
    isSendBusy: input.threadRuntime.dispatch.isSendBusy,
    isConnecting: runtimeFlags.isConnecting,
    composerDraftTarget: bootstrap.composerDraftTarget,
    composerReady: composer.composerReady,
    composerDisabledReason: input.composerDisabledReason,
    composerRef: composer.composerRef,
    setPendingAutoSubmit: composer.setComposerPendingAutoSubmit,
  };
}

function buildProviderModelInput(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly lockedProvider: ProviderKind | null;
  readonly composerModelSelection: ComposerModelSelection;
}) {
  const { bootstrap, composer, settings, stickyModelSelection } = input.routeRuntime;
  return {
    activeThread: bootstrap.activeThread,
    currentModelSelection: input.composerModelSelection.modelSelection,
    lockedProvider: input.lockedProvider,
    providerStatuses: bootstrap.providerStatuses,
    settings,
    scheduleComposerFocus: composer.scheduleComposerFocus,
    setComposerDraftModelSelection: composer.setComposerDraftModelSelection,
    setStickyComposerModelSelection: stickyModelSelection,
  };
}
